import React, { useCallback, useMemo, useRef, useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { createReactEditorJS } from 'react-editor-js';
import Header from '@editorjs/header';
import List from '@editorjs/list';
import ImageTool from '@editorjs/image';
import LinkTool from '@editorjs/link';
import Button from '@mui/material/Button';
import { Divider } from "antd";
import { UPLOAD_URL, FETCH_RESOURCE_URL } from "../../configs/Domain";
import { getAuthHeader } from "../../store/utils";
import { WarningSharp } from "@mui/icons-material";
import { v4 as uuidv4 } from 'uuid';

const ReactEditorJS = createReactEditorJS(); // This creates the React component

const createInitialEditorBlock = () => {
    return {
        id: uuidv4(),
        type: 'header',
        data: {
            text: 'You can start typing here..\r\n\r\nYou can also upload images and files directly to the editor'
        }
    };
};

const ContentEditor = forwardRef(({ initialData, onContentChange }, ref) => {
    const editorCore = useRef(null);  // useRef to hold the editor instance
    const [hasChanges, setHasChanges] = useState(false);

    const EDITOR_JS_TOOLS = useMemo(() => ({
        header: {
            class: Header,
            config: {
                placeholder: 'Enter a header', // Add a placeholder for the header
                levels: [1, 2, 3], // Specify the header levels you want to support
            },
        },
        list: List,
        image: {
            class: ImageTool,
            config: {
                endpoints: {
                    byFile: UPLOAD_URL,
                    byUrl: FETCH_RESOURCE_URL,
                },
                resizeHandles: true, // Show resize handles
                resizeMinWidth: 100, // Minimum width (optional)
                resizeMinHeight: 100, // Minimum height (optional)
                additionalRequestHeaders: getAuthHeader(),
            }
        },
        linkTool: {
            class: LinkTool,
            config: {
                endpoint: FETCH_RESOURCE_URL, // Your backend endpoint for url data fetching,
            }
        }
    }), []);

    useImperativeHandle(ref, () => ({
        updateContent: (newContent) => {
            console.log('Updating content from parent', newContent)
            if (editorCore.current) {
                editorCore.current.render(newContent);
            }
        }
    }));

    const handleInitialize = useCallback((instance) => {
        editorCore.current = instance;
        // Initialize with a placeholder block if the editor is empty
    }, [initialData]);

    const handleSave = useCallback(async () => {
        if (editorCore.current) {
            try {
                const savedData = await editorCore.current.save();
                onContentChange(savedData);  // Handle the saved data, e.g., update the state or send to the backend
                setHasChanges(false);  // Reset the state flag after
            } catch (error) {
            }
        }
    }, [onContentChange]);  // Depend on the onContentChange to ensure it's captured in the closure if it updates

    const handleChange = useCallback(() => {
        setHasChanges(true);  // Set the state flag to true on change
    }, []);

    return (
        <div style={{ width: '100%' }}>
            {hasChanges && (
                <>
                    <Button startIcon={<WarningSharp />} onClick={handleSave} variant="text" color="error">
                        New Changes (Save by clicking here before leaving)
                    </Button>
                    <Divider />
                </>
            )}
            <ReactEditorJS
                onInitialize={handleInitialize}
                tools={EDITOR_JS_TOOLS}
                defaultValue={!initialData ? {
                    blocks: [createInitialEditorBlock()]
                } : initialData}
                onChange={handleChange}
            />
        </div>
    );
});

export default ContentEditor;
